import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import heroMandala from "../../assets/Images/TempleDetail/hero-mandala.svg";
import borderLeft from "../../assets/Images/TempleDetail/hero-border-left.svg";
import borderRight from "../../assets/Images/TempleDetail/hero-border-right.svg";
import chipLocation from "../../assets/Images/TempleDetail/chip-location.svg";
import chipTemple from "../../assets/Images/TempleDetail/chip-temple.svg";
import shareIcon from "../../assets/Images/TempleDetail/share.svg";
import progressMandir from "../../assets/Images/TempleDetail/progress-mandir.svg";
import statManaged from "../../assets/Images/TempleDetail/stat-managed.svg";
import statAge from "../../assets/Images/TempleDetail/stat-age.svg";
import statDevotees from "../../assets/Images/TempleDetail/stat-devotees.svg";
import statStatus from "../../assets/Images/TempleDetail/stat-status.svg";
import arrowRightWhite from "../../assets/Images/TempleDetail/arrow-right-white.svg";
import { T, FONT, HINDI_FONT, Rupee, Photo } from "./shared";

const CHIP_ICONS = { location: chipLocation, temple: chipTemple };
const FACT_ICONS = { managed: statManaged, age: statAge, devotees: statDevotees, status: statStatus };
const AVATAR_COLORS = ["#F04951", "#C9952B", "#3B82F6"];

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "TH";

// Figma 3422:5927 — photo card with the maroon wash, and the stats card that rides over its foot.
const TempleHero = ({ temple, donorNames = [], onShare, onDonate }) => {
  const isDesktop = useMediaQuery("(min-width:1001px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const avatars = (donorNames.length ? donorNames : ["P S", "R A", "S F"]).slice(0, 3);
  // Web: every temple uses the compact title sizing (42px title / 20px subtitle) — the 60px size made
  // short names like "Radha Krishna Shiv Temple" look oversized next to the rest of the banner.
  // Mobile keeps stepping down only for long names.
  const longTitle = !isMobile || String(temple.name || "").length > 28;

  // What the stats card shows (admin hide toggles). Hide All drops the amounts + bar but keeps the facts.
  const showTopRow = temple.showProgress && (temple.showRaised || temple.showGoal);
  const hasCard = temple.showProgress || temple.facts.length > 0;
  // Desktop: the card overlaps the photo from 442px; the maroon foot grows/shrinks with the card's
  // real height, so hiding rows never leaves a gap or clips the card.
  const CARD_TOP = 442;
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(208);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setCardH(Math.round(el.offsetHeight)));
    ro.observe(el);
    setCardH(Math.round(el.offsetHeight));
    return () => ro.disconnect();
  }, [isDesktop, hasCard]);
  const desktopPhotoH = hasCard ? Math.max(560, CARD_TOP + cardH + 9) : 560;

  const photoCard = (
    <Box
      sx={{
        position: "relative",
        background: T.maroon,
        borderRadius: isMobile ? "20px" : "28px",
        overflow: "hidden",
        // Desktop: the maroon foot holds the overlapping stats card — shorter when there are no facts
        height: isDesktop ? `${desktopPhotoH}px` : isMobile ? "420px" : "520px",
      }}
    >
      <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, height: isDesktop ? "560px" : "100%" }}>
        <Photo src={temple.heroImage} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: isMobile
              ? "linear-gradient(180deg, rgba(43,8,10,0.25) 0%, rgba(43,8,10,0.6) 45%, rgba(43,8,10,0.96) 100%)"
              : "linear-gradient(90deg, rgba(43,8,10,0.96) 0%, rgba(43,8,10,0.72) 42%, rgba(43,8,10,0.05) 78%, rgba(43,8,10,0) 100%)",
          }}
        />
        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }} />
      </Box>

      {/* Mandala + gold border ornaments */}
      <Box sx={{ position: "absolute", left: "-120px", top: "-110px", width: "420px", height: "420px", opacity: 0.14, pointerEvents: "none" }}>
        <Box component="img" src={heroMandala} alt="" sx={{ position: "absolute", inset: "2.5%", width: "95%", height: "95%" }} />
      </Box>
      {!isMobile && (
        <>
          <Box component="img" src={borderLeft} alt="" sx={{ position: "absolute", left: "36px", top: "20px", width: "13.5px", height: isDesktop ? "520px" : "460px", opacity: 0.45, pointerEvents: "none" }} />
          <Box component="img" src={borderRight} alt="" sx={{ position: "absolute", right: "36px", top: "23px", width: "13.5px", height: isDesktop ? "513px" : "455px", pointerEvents: "none" }} />
        </>
      )}

      {/* Share */}
      <Box
        component="button"
        type="button"
        onClick={onShare}
        sx={{
          position: "absolute",
          top: isMobile ? "14px" : "20px",
          right: isMobile ? "14px" : "59px",
          height: "43px",
          pl: "16px",
          pr: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "100px",
          border: "1px solid rgba(255,255,255,0.22)",
          background: "rgba(31,8,10,0.62)",
          backdropFilter: "blur(7px)",
          boxShadow: "0px 6px 18px 0px rgba(0,0,0,0.18)",
          color: "#FFFFFF",
          cursor: "pointer",
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "18px",
          "&:hover": { background: "rgba(31,8,10,0.78)" },
          "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
        }}
      >
        <Box component="img" src={shareIcon} alt="" sx={{ width: "16px", height: "16px" }} />
        Share
      </Box>

      {/* Copy */}
      <Box
        sx={{
          position: "absolute",
          left: isMobile ? "20px" : "84px",
          right: isMobile ? "20px" : "auto",
          // Anchored from the bottom so a long campaign title grows upward instead of
          // sliding under the stats card (desktop: the card starts 205px above the photo's foot).
          top: "auto",
          // desktop: always 51px above the card's top edge, whatever the card's height
          bottom: isMobile ? "48px" : isDesktop ? `${desktopPhotoH - (CARD_TOP - 51)}px` : "56px",
          maxWidth: "640px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {temple.tagline && (
          <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "16px" : "20px", lineHeight: "28px", color: T.goldLight }}>{temple.tagline}</Typography>
        )}
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            // Design: 60px for a short temple name. Longer campaign titles step down and stop at 3 lines.
            fontSize: isMobile ? (longTitle ? "26px" : "32px") : isDesktop ? (longTitle ? "42px" : "60px") : longTitle ? "34px" : "46px",
            lineHeight: isMobile ? (longTitle ? "32px" : "40px") : isDesktop ? (longTitle ? "50px" : "68px") : longTitle ? "42px" : "54px",
            color: "#FFFFFF",
            mt: isMobile ? "4px" : "6px",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {temple.name}
        </Typography>
{temple.subtitle && (
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "16px" : longTitle ? "20px" : "24px", lineHeight: 1.3, color: "rgba(255,255,255,0.82)", mt: isMobile ? "12px" : longTitle ? "16px" : "26px", maxWidth: "604px" }}>
          {temple.subtitle}
        </Typography>
        )}
        {temple.chips.length > 0 && (
        <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mt: isMobile ? "16px" : longTitle ? "22px" : "34px" }}>
          {temple.chips.map((chip) => (
            <Box key={chip.icon} sx={{ display: "flex", alignItems: "center", gap: "7px", px: "14px", py: "8px", borderRadius: "100px", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)" }}>
              <Box component="img" src={CHIP_ICONS[chip.icon]} alt="" sx={{ width: "15px", height: "15px" }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "13px", lineHeight: "18px", color: "#FFFFFF", whiteSpace: "nowrap" }}>{chip.label}</Typography>
            </Box>
          ))}
        </Box>
        )}
      </Box>

      {temple.taxBenefit && (
        <Box sx={{ position: "absolute", top: isMobile ? "14px" : "20px", left: isMobile ? "14px" : "84px", background: "linear-gradient(106.41deg, #EC1B26 5.07%, #FF4A60 106.05%)", borderRadius: "4px", px: "14px", py: "8px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: 1, color: "#FFFFFF" }}>{temple.taxBenefit}</Typography>
        </Box>
      )}
    </Box>
  );

  const fillPct = temple.pct;
  const statsCard = hasCard && (
    <Box
      ref={cardRef}
      sx={{
        position: isDesktop ? "absolute" : "relative",
        left: isDesktop ? "50%" : "auto",
        transform: isDesktop ? "translateX(-50%)" : "none",
        top: isDesktop ? "442px" : "auto",
        width: isDesktop ? "calc(100% - 200px)" : "auto",
        maxWidth: "1240px",
        mt: isDesktop ? 0 : isMobile ? "-28px" : "-40px",
        mx: isDesktop ? 0 : isMobile ? "12px" : "30px",
        background: "#FFFFFF",
        border: `1px solid ${T.sand}`,
        borderRadius: "22px",
        boxShadow: "0px 16px 40px 0px rgba(64,13,13,0.14)",
        // Figma 3422:6024 (1240×196) with more room above the amount row: 22 top · 38 amount row · 18 · 38 bar row · 12 · divider · 19 · 40 facts · 20 bottom
        px: isMobile ? "18px" : "36px",
        pt: isMobile ? "16px" : "22px",
        pb: isMobile ? "18px" : "20px",
        boxSizing: "border-box",
        zIndex: 2,
      }}
    >
      {/* Raised / donors / % funded */}
      {showTopRow && (
      <Box sx={{ display: "flex", alignItems: isDesktop ? "flex-end" : "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", minHeight: "38px" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
          {temple.showRaised && (
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "24px" : "30px", lineHeight: "38px", color: T.ink, whiteSpace: "nowrap" }}>
            <Rupee value={temple.raised} />
          </Typography>
          )}
          {temple.showGoal && (
            <Typography sx={{ fontFamily: FONT, fontWeight: temple.showRaised ? 500 : 700, fontSize: temple.showRaised ? "14px" : "18px", lineHeight: temple.showRaised ? "20px" : "38px", color: temple.showRaised ? T.muted : T.ink, whiteSpace: "nowrap" }}>
              {temple.showRaised ? "raised of " : "Goal "}<Rupee value={temple.goal} />
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", ml: "auto" }}>
          {temple.showRaised && (
          <>
          <Box sx={{ display: "flex" }}>
            {avatars.map((name, i) => (
              <Box key={i} sx={{ width: "26px", height: "26px", borderRadius: "13px", border: "2px solid #FFFFFF", background: AVATAR_COLORS[i], ml: i ? "-8px" : 0, zIndex: 3 - i, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: "12px", color: "#FFFFFF" }}>{initials(name)}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "14px", lineHeight: "20px", color: T.ink, whiteSpace: "nowrap" }}>
            {temple.backers.toLocaleString("en-IN")} donors
          </Typography>
          </>
          )}
          {temple.showGoal && (
            <>
              {temple.showRaised && <Box sx={{ width: "1px", height: "20px", background: T.sand }} />}
              <Box sx={{ background: "rgba(236,27,38,0.1)", borderRadius: "100px", px: "12px", py: "5px" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "13px", lineHeight: "17px", color: T.red, whiteSpace: "nowrap" }}>{fillPct}% funded</Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
      )}

      {/* Bar + Donate Now (hidden by Hide All) */}
      {temple.showProgress && (
      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "12px" : "8px", minHeight: isDesktop ? "38px" : "auto", mt: !showTopRow ? 0 : isMobile ? "16px" : "18px" }}>
        <Box sx={{ position: "relative", flex: 1, height: "30px", display: "flex", alignItems: "center" }}>
          <Box sx={{ position: "absolute", left: 0, right: 0, height: "10px", borderRadius: "5px", background: "#F1ECE6" }} />
          <Box sx={{ position: "absolute", left: 0, width: `${fillPct}%`, height: "10px", borderRadius: "5px", background: "linear-gradient(90deg, #F7787E 0%, #EC1B26 100%)", transition: "width 0.6s ease" }} />
          <Box
            sx={{
              position: "absolute",
              left: `clamp(0px, calc(${fillPct}% - 15px), calc(100% - 30px))`,
              width: "30px",
              height: "30px",
              boxSizing: "border-box",
              borderRadius: "15px",
              border: "3px solid #FFFFFF",
              background: T.red,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0px 2px 6px rgba(236,27,38,0.3)",
            }}
          >
            <Box component="img" src={progressMandir} alt="" sx={{ width: "14px", height: "14px" }} />
          </Box>
        </Box>
        {isDesktop && (
          <Box
            component="button"
            type="button"
            onClick={onDonate}
            sx={{
              height: "38px",
              pl: "24px",
              pr: "20px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "100px",
              border: `1px solid ${T.coral}`,
              background: T.red,
              color: "#FFFFFF",
              cursor: "pointer",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "20px",
              whiteSpace: "nowrap",
              flexShrink: 0,
              ml: "0px",
              "&:hover": { background: "#D41620" },
              "&:focus-visible": { outline: `2px solid ${T.ink}`, outlineOffset: "2px" },
            }}
          >
            Donate Now
            <Box component="img" src={arrowRightWhite} alt="" sx={{ width: "15px", height: "15px" }} />
          </Box>
        )}
      </Box>
      )}

      {temple.showProgress && temple.facts.length > 0 && <Box sx={{ height: "1px", background: T.sand, mt: isDesktop ? "12px" : "16px" }} />}

      {/* Facts row (only the facts admin filled in) */}
      {temple.facts.length > 0 && (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : `repeat(${temple.facts.length}, 1fr)`,
          rowGap: "16px",
          // equal space above and below the facts (design: 19 / 20)
          mt: !temple.showProgress ? 0 : isDesktop ? "19px" : "20px",
        }}
      >
        {temple.facts.map((fact, i) => (
          <Box
            key={fact.key}
            sx={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              minWidth: 0,
              minHeight: "40px",
              justifyContent: isMobile ? "flex-start" : "center",
              px: isMobile ? 0 : "12px",
              borderLeft: !isMobile && i > 0 ? `1px solid ${T.sand}` : "none",
            }}
          >
            <Box sx={{ width: "38px", height: "38px", borderRadius: "11px", background: "rgba(242,178,186,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Box component="img" src={FACT_ICONS[fact.key]} alt="" sx={{ width: "18px", height: "18px" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: "13px", letterSpacing: "0.8px", color: T.muted, textTransform: "uppercase" }}>{fact.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "13px" : "14px", lineHeight: "19px", color: T.ink, mt: "3px" }}>{fact.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ position: "relative", maxWidth: "1440px", mx: "auto", px: isMobile ? "12px" : isDesktop ? "40px" : "20px", pt: "20px", boxSizing: "border-box" }}>
      {photoCard}
      {statsCard}
    </Box>
  );
};

export default TempleHero;
